import { PAX_TYPE } from '@app/enums';
import { Passenger } from '@app/models/micro/passenger.model';
import { Reservation } from '@app/models/micro/reservation.model';

/**
 * CI-23290: Declaracion de salud CDC (RDC, 21 dias) para vuelos que llegan a USA.
 * Funciones puras para decidir cuando se muestra el modal y como leer la respuesta de BE.
 */

/** Formulario oficial del CDC que abre el QR de "Ver declaracion completa". */
export const CDC_ATTESTATION_URL =
  'https://www.cdc.gov/port-health/media/pdfs/2026/10/367886-A_FRM_EbolaAttestation_3_pass.pdf';

/** Campos que BE regresa en fieldsUpdate / fieldsFailed para la atestacion. */
export const CDC_ATTESTATION_FIELDS = ['ATTESTATION_ADOC', 'ATTESTATION_ATOC'];

const US_COUNTRY_CODES = ['US', 'USA'];

/**
 * BE asienta ADOC y ATOC juntos. ATOC solo lo genera la atestacion, por eso es el que
 * indica que el pasajero ya declaro (ADOC tambien lo usan agentes para SKY CLEARED).
 */
export function hasCdcAttestation(passenger: Passenger): boolean {
  return !!passenger?.ssrs?.some((ssr) => ssr?.code === 'ATOC');
}

/** Algun segmento del tramo llega a USA (destino o conexion). */
export function legArrivesInUsa(reservation: Reservation): boolean {
  const segments = reservation?.flightSummary?.segments ?? [];
  return segments.some((segment) =>
    US_COUNTRY_CODES.includes((segment?.arrivalCountry || '').toUpperCase())
  );
}

/** Pasajeros que llevan fila en el modal: adultos y menores. El infante va con su adulto. */
export function getCdcAttestationPassengers(reservation: Reservation): Passenger[] {
  return (reservation?.passengers ?? []).filter((p) => p.paxType !== PAX_TYPE.INFANT);
}

/**
 * El modal aplica si el flag esta encendido, el tramo llega a USA, no es seamless
 * (el responsable es el operating carrier) y al menos un pasajero no ha declarado.
 */
export function shouldShowCdcAttestation(reservation: Reservation, flagEnabled: boolean): boolean {
  if (!flagEnabled || !reservation || reservation.codeShare) {
    return false;
  }
  if (!legArrivesInUsa(reservation)) {
    return false;
  }
  return getCdcAttestationPassengers(reservation).some((p) => !hasCdcAttestation(p));
}

/**
 * La atestacion se guardo solo si BE confirma los campos ATTESTATION_* en fieldsUpdate
 * y ninguno viene en fieldsFailed. Sin confirmacion se trata como error.
 */
export function isCdcAttestationSaved(response: any, passengerNameNumber: string): boolean {
  const passengers: any[] = response?.passengers ?? [];
  const pax = passengers.find((p) => p?.passengerNameNumber === passengerNameNumber);
  if (!pax) {
    return false;
  }
  const failed: string[] = pax.fieldsFailed ?? [];
  if (failed.some((field) => CDC_ATTESTATION_FIELDS.includes(field))) {
    return false;
  }
  const updated: string[] = pax.fieldsUpdate ?? [];
  return CDC_ATTESTATION_FIELDS.every((field) => updated.includes(field));
}
